import Flickity from "flickity";

/**
 * Product Filters Carousel
 * @param {object} opts
 */
function productsCarousel(opts) {
  const config = {
    dom: {
      wrapper: "js-products-carousel-wrapper",
      carousel: "js-products-carousel",
      carouselCell: "js-products-carousel-cell",
      pi: "js-pi",
      shopAll: "js-shop-all"
    },
    cls: {
      active: "is-active",
      fullHeight: "full-height",
      hide: "hide"
    }
  };
  const c = __.extend(config, opts);

  const flickityOpts = {
    contain: true,
    draggable: true,
    dragThreshold: 5,
    pageDots: true,
    prevNextButtons: true,
    wrapAround: true,
    cellAlign: "center",
    lazyload: true,
    accessibility: false
  };
  const flickityMobileOpts = {
    contain: true,
    draggable: true,
    dragThreshold: 5,
    pageDots: true,
    prevNextButtons: true,
    wrapAround: true,
    cellAlign: "center",
    lazyload: true,
    selectedAttraction: 0.4,
    friction: 0.8,
    accessibility: false
  };

  // global elements
  const { template } = SDG.Data || false;
  const isCartPage = template === "cart" || false;
  const $wrappers = document.body.querySelectorAll(`.${c.dom.wrapper}`);
  const flickityCarousels = [];
  const cellVisibleRatio = 0.5;
  let view;

  /**
   * On Window Resize
   */
  function onWindowResize() {
    // desktop
    __.mq({
      view: "desktop",
      callback: () => {
        if (view !== "desktop") {
          view = "desktop";
          rebuildFlickity();
        }
      }
    });

    // mobile
    __.mq({
      view: "mobile",
      callback: () => {
        if (view !== "mobile") {
          view = "mobile";
          rebuildFlickity();
        }
      }
    });
  }

  function resizeFlickity(height = 0) {
    [...$wrappers].forEach(($wrapper, index) => {
      const activeCarousel = flickityCarousels[index];
      if (activeCarousel) { 
        const $viewport = activeCarousel.viewport;
        const $carousel = $wrapper.querySelector(`.${c.dom.carousel}`);
        const cells = $carousel?.querySelectorAll(`.${c.dom.carouselCell}`);
        
        if (!$viewport) return;
        
        const viewportHeight = $viewport.clientHeight;
        
        // If height is provided, use it
        if (height > 0) {
          if (viewportHeight < height) {
            $viewport.style.height = `${height}px`;
            activeCarousel.resize();
          }
        } else if (viewportHeight === 0 && cells && cells.length > 0) {
          // If viewport height is 0, calculate from cells
          const maxCellHeight = calculateMaxCellHeight(cells);
          if (maxCellHeight > 0) {
            $viewport.style.height = `${maxCellHeight}px`;
            activeCarousel.resize();
          }
        }

        syncCellAccessibility(activeCarousel);
      }
    });
  }


  /**
   * Calculate maximum cell height
   */
  function calculateMaxCellHeight(cells) {
    const isMobile = view === "mobile";
    const padding = isMobile ? 21 : 0;
    let maxHeight = 0;
    [...cells].forEach(($cell) => {
      // Reset height temporarily to get natural height
      const originalHeight = $cell.style.height;
      $cell.style.height = "auto";
      const cellHeight = $cell.offsetHeight;
      $cell.style.height = originalHeight;

      if (cellHeight > maxHeight) {
        maxHeight = cellHeight;
      }
    });
    return maxHeight + padding;
  }

  function getCellFocusables($cell) {
    return [...$cell.querySelectorAll("a[href], button:not([disabled])")];
  }

  function getCellVisibleRatio($cell, $viewport) {
    const cellRect = $cell.getBoundingClientRect();
    const viewportRect = $viewport.getBoundingClientRect();
    const visibleWidth =
      Math.min(cellRect.right, viewportRect.right) - Math.max(cellRect.left, viewportRect.left);

    return cellRect.width > 0 ? visibleWidth / cellRect.width : 0;
  }

  function getVisualCells(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) return [];

    return flickity.cells
      .map((cell) => cell.element)
      .filter(($cell) => getCellVisibleRatio($cell, $viewport) >= cellVisibleRatio)
      .sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
  }

  function getFocusedCell(flickity) {
    if (!flickity.element.contains(document.activeElement)) return null;

    return document.activeElement.closest(`.${c.dom.carouselCell}`);
  }

  function setCellTabEntry(flickity, $entry) {
    flickity.cells.forEach((cell) => {
      getCellFocusables(cell.element).forEach(($el) => {
        $el.tabIndex = $el === $entry ? 0 : -1;
      });
    });
  }

  function syncCellAccessibility(flickity) {
    const $viewport = flickity?.viewport;
    if (!$viewport || !flickity.cells?.length) return;

    const $visualCells = getVisualCells(flickity);
    const $focusedCell = getFocusedCell(flickity);

    flickity.cells.forEach((cell) => {
      const $cell = cell.element;

      if ($visualCells.includes($cell) || $cell === $focusedCell) {
        $cell.removeAttribute("aria-hidden");
        $cell.inert = false;
      } else {
        $cell.setAttribute("aria-hidden", "true");
        $cell.inert = true;
      }
    });

    const $focused = $focusedCell ? document.activeElement : null;
    const [$entryFocusable] = $visualCells.length ? getCellFocusables($visualCells[0]) : [];

    setCellTabEntry(flickity, $focused || $entryFocusable);
  }

  function handleCellTab(e, flickity) {
    if (e.key !== "Tab") return;

    // lazy loaded images resize the cells, so the state is refreshed before acting on it
    syncCellAccessibility(flickity);

    const $visualCells = getVisualCells(flickity);
    if (!$visualCells.length) return;

    const $currentCell = e.target.closest(`.${c.dom.carouselCell}`);
    const currentIndex = $visualCells.indexOf($currentCell);

    if (currentIndex === -1) {
      const [$prevButton] = flickity.element.querySelectorAll(".flickity-prev-next-button");

      // shift-tabbing back into the carousel lands on the rightmost visible cell
      if (e.shiftKey && e.target === $prevButton) {
        const lastFocusables = getCellFocusables($visualCells[$visualCells.length - 1]);
        const $target = lastFocusables[lastFocusables.length - 1];

        if ($target) {
          e.preventDefault();
          $target.focus({ preventScroll: true });
        }
      }

      return;
    }

    const focusables = getCellFocusables($currentCell);
    const focusIndex = focusables.indexOf(e.target.closest("a[href], button"));
    const $nextInCell = focusables[e.shiftKey ? focusIndex - 1 : focusIndex + 1];

    if (focusIndex !== -1 && $nextInCell) {
      e.preventDefault();
      $nextInCell.focus({ preventScroll: true });
      return;
    }

    const $nextCell = $visualCells[e.shiftKey ? currentIndex - 1 : currentIndex + 1];
    if (!$nextCell) return;

    const nextFocusables = getCellFocusables($nextCell);
    const $target = e.shiftKey ? nextFocusables[nextFocusables.length - 1] : nextFocusables[0];
    if (!$target) return;

    e.preventDefault();
    $target.focus({ preventScroll: true });
  }

  function handleCellFocus(e, flickity) {
    if (!e.target.closest(`.${c.dom.carouselCell}`)) return;

    if (flickity.viewport.scrollLeft !== 0) flickity.viewport.scrollLeft = 0;

    setCellTabEntry(flickity, e.target);
  }

  function bindCellAccessibility(flickity) {
    if (!flickity) return;

    const sync = () => syncCellAccessibility(flickity);

    sync();
    flickity.on("ready", sync);
    flickity.on("select", sync);
    flickity.on("settle", sync);
    flickity.on("lazyLoad", sync);

    const onKeydown = (e) => handleCellTab(e, flickity);
    const onFocusin = (e) => handleCellFocus(e, flickity);

    if (flickity.element._cellTabHandler) {
      flickity.element.removeEventListener("keydown", flickity.element._cellTabHandler);
    }
    if (flickity.element._cellFocusHandler) {
      flickity.element.removeEventListener("focusin", flickity.element._cellFocusHandler);
    }

    flickity.element._cellTabHandler = onKeydown;
    flickity.element._cellFocusHandler = onFocusin;
    flickity.element.addEventListener("keydown", onKeydown);
    flickity.element.addEventListener("focusin", onFocusin);
  }

  function clearCellAccessibility(cells) {
    [...cells].forEach(($cell) => {
      $cell.removeAttribute("aria-hidden");
      $cell.inert = false;
      getCellFocusables($cell).forEach(($el) => $el.removeAttribute("tabindex"));
    });
  }
  
  /**
   * rebuild flickity
   */
  function rebuildFlickity(isEventDriven = false) {
    const carouselOpts = view === "mobile" ? flickityMobileOpts : flickityOpts;

    [...$wrappers].forEach(($wrapper, index) => {
      const activeCarousel = flickityCarousels[index];
      if (activeCarousel) activeCarousel.destroy();

      const $carousel = $wrapper.querySelector(`.${c.dom.carousel}`);
      const cells = $carousel.querySelectorAll(`.${c.dom.carouselCell}`);

      if (isEventDriven) {
        // reset carousel position to avoid shifting
        $carousel.style.left = 0;
      }

      [...cells].forEach(($item) => {
        __.removeClass($item, c.cls.fullHeight);
      });
      clearCellAccessibility(cells);

      // only build carousel if there are more than 4 cells
      if (!$carousel || cells.length < 4) {
        const $shopAll = $wrapper.querySelector(`.${c.dom.shopAll}`);
        $carousel.style.opacity = 1;
        if ($shopAll) {__.addClass($shopAll, c.cls.hide);}
        return;
      }

      const maxCellHeight = calculateMaxCellHeight(cells);
      
      carouselOpts.on = {
        ready: () => {
          [...cells].forEach(($item) => {
            __.addClass($item, c.cls.fullHeight);
          });

          if (isEventDriven) {
            // reset carousel position to avoid shifting
            $carousel.style.removeProperty("left");
          }

          // Ensure viewport has correct height
          const newCarousel = flickityCarousels[index];
          if (newCarousel && newCarousel.viewport) {
            const viewportHeight = newCarousel.viewport.clientHeight;
            if (viewportHeight === 0 && maxCellHeight > 0) {
              newCarousel.viewport.style.height = `${maxCellHeight}px`;
              newCarousel.resize();
            }
            syncCellAccessibility(newCarousel);
          }

          window.dispatchEvent(new CustomEvent("product-view-changed"));
        }
      };

      // // Set the offset divisor based on viewport size
      // let offsetDivisor;
      // if (view === 'desktop') {
      //   offsetDivisor = 1.33;
      // }

      // if (view === 'mobile') {
      //   offsetDivisor = 3;
      // }

      // const $flickityCell = $carousel.querySelector(`.${c.dom.carouselCell}`);
      // const leftOffset = setFlickityOffset($flickityCell.offsetWidth, offsetDivisor);
      // const carouselOffset = $carousel.offsetWidth;
      // const cellAlign = leftOffset / carouselOffset;
      // const updatedFlickityOpts = __.extend(flickityOpts, { cellAlign });

      setTimeout(() => {
        if (activeCarousel) {
          flickityCarousels[index] = new Flickity($carousel, carouselOpts);
        } else {
          flickityCarousels.push(new Flickity($carousel, carouselOpts));
        }

        // Force resize after initialization to ensure correct height
        const newCarousel = flickityCarousels[index];
        if (newCarousel) {
          bindCellAccessibility(newCarousel);

          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            if (newCarousel.viewport) {
              const viewportHeight = newCarousel.viewport.clientHeight;
              if (viewportHeight === 0 && maxCellHeight > 0) {
                newCarousel.viewport.style.height = `${maxCellHeight}px`;
                newCarousel.resize();
              } else if (viewportHeight > 0) {
                // Ensure all cells have the same height
                newCarousel.resize();
              }
              syncCellAccessibility(newCarousel);
            }
          });
        }
      }, 500); // short timeout for Flickity cell sizing
    });
  }

  // /**
  //  * Set the flickity carousel offset
  //  */
  // function setFlickityOffset(flickityCellOffsetWidth, flickityOffsetDivisor) {
  //   Flickity.Cell.prototype.setDefaultTarget = () => {
  //     const marginProperty = this.parent.originSide === 'left' ? 'marginLeft' : 'marginRight';
  //     this.target = this.x + this.size[marginProperty];
  //   };

  //   return flickityCellOffsetWidth / flickityOffsetDivisor;
  // }

  /**
   * Init
   */
  function init() {
    if (!$wrappers?.length) return;

    if (isCartPage) {
      // small timeout to allow for cart page to load
      setTimeout(() => {
        onWindowResize();
        __.windowResize(onWindowResize);
      }, 500);
    } else {
      onWindowResize();
      __.windowResize(onWindowResize);
    }

    document.removeEventListener("resizeFlickity", () => resizeFlickity());
    document.addEventListener("resizeFlickity", (event) => resizeFlickity(event.detail.height));
  }

  return init();
}

export default productsCarousel;
